import { defineStore } from 'pinia'
import router from '../router.js'
import tooManagement from '../api/tooManagement'
import { postImage } from '../api/common'
import uuidv4 from '../utils/uuidv4.js'
import Storyblok from '../storyblok'

const emptySite = {
  id: null,
  googleTagManagerId: 'GTM-5XD343G', // Default workspace in GTM
}

export const useSiteStore = defineStore('siteStore', {
  state: () => ({
    isLoading: false,
    isSaving: false,
    errors: [],
    tagGroupsCategories: [
      { value: 'VACANCY_EDUCATION', name: 'Education' },
      { value: 'VACANCY_WORKFIELD', name: 'Work field' },
      { value: 'VACANCY_WORKCATEGORY', name: 'Work category' },
      { value: 'VACANCY_WORKEXPERIENCE', name: 'Work experience' },
      { value: 'VACANCY_EMPLOYMENTTYPE', name: 'Employment type' },
    ],
    tagGroups: [],
    tags: [],
    tagErrors: [],
    isSavedSuccessfully: false,
    site: { ...emptySite },
    storyblokLink: null,
    fullLogoImage: null,
    faviconImage: null,
  }),
  actions: {
    async load(siteId) {
      this.isLoading = true
      this.resetState()
      try {
        if (siteId === 'new') {
          await this.setupNewSite()
        } else {
          await this.loadExistingSite(siteId)
          await this.getStoryblokLink()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    async loadTagGroups() {
      this.tagGroups = []
      this.tagErrors = []
      this.isLoading = true
      try {
        const tagGroups = await tooManagement.getTagGroups(this.site.id)
        this.tagGroups = tagGroups.data
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    async loadTags(tagGroupId) {
      this.tags = []
      this.tagErrors = []
      this.isLoading = true
      try {
        const tags = await tooManagement.getTags(tagGroupId)
        this.tags = tags.data
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    async addTagGroup(tagGroup) {
      this.tagErrors = []
      this.isSaving = true
      try {
        const savedGroup = await tooManagement.postTagGroup(
          this.site.id,
          tagGroup,
        )
        this.tagGroups.push(savedGroup.data)
        this.isSavedSuccessfully = true
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isSaving = false
      }
    },
    async editTagGroup(tagGroup) {
      this.tagErrors = []
      this.isSaving = true
      try {
        const savedGroup = await tooManagement.putTagGroup(
          this.site.id,
          tagGroup,
        )
        const index = this.tagGroups.findIndex(
          (group) => group.id === savedGroup.data.id,
        )
        this.tagGroups[index] = savedGroup.data
        this.isSavedSuccessfully = true
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isSaving = false
      }
    },
    async editTag(tag, tagGroupId) {
      this.tagErrors = []
      this.isSaving = true
      try {
        const savedTag = await tooManagement.putTag(tag, tagGroupId)
        const index = this.tags.findIndex((tag) => tag.id === savedTag.data.id)
        this.tags[index] = savedTag.data
        this.isSavedSuccessfully = true
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isSaving = false
      }
    },
    async addTag(tag, tagGroupId) {
      this.tagErrors = []
      this.isSaving = true
      try {
        const savedTag = await tooManagement.postTag(tag, tagGroupId)
        this.tags.push(savedTag.data)
        this.isSavedSuccessfully = true
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isSaving = false
      }
    },
    async save() {
      this.isSaving = true
      this.errors = []
      this.isSavedSuccessfully = false

      try {
        await this.saveImages()
        await this.saveSiteData()
        await this.getStoryblokLink()
        this.isSavedSuccessfully = true
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isSaving = false
      }
    },
    async saveImages() {
      try {
        if (this.fullLogoImage) {
          const fullLogoUrl = await this.uploadImage(this.fullLogoImage)
          this.site.fullLogoUrl = fullLogoUrl.data.url
        }
        if (this.faviconImage) {
          const faviconUrl = await this.uploadImage(this.faviconImage)
          this.site.faviconUrl = faviconUrl.data.url
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    async uploadImage(image) {
      return postImage('management/v1/images', image)
    },
    async saveSiteData() {
      const siteToSave = { ...this.site }
      const saveFunction =
        this.site.id === 'new' ? tooManagement.postSite : tooManagement.putSite
      if (this.site.id === 'new') {
        delete siteToSave.id // Remove 'id' for new site
      }
      const savedSite = await saveFunction(siteToSave)
      if (this.site.id === 'new') {
        router.replace(`/sites/${savedSite.data.id}/site`)
      }
      this.site = savedSite.data
      this.isSavedSuccessfully = true
    },
    async setupNewSite() {
      this.site = {
        ...emptySite,
        token: uuidv4(),
        id: 'new',
      }
    },
    async loadExistingSite(siteId) {
      const site = await tooManagement.getSite(siteId)
      this.site = site.data
      await this.loadTagGroups()
    },
    async getStoryblokLink() {
      try {
        if (this.site && this.site?.id !== 'new') {
          const [spaceResponse, storiesResponse] = await Promise.all([
            Storyblok.get('cdn/spaces/me', {
              token:
                this.site.storyblokToken ||
                import.meta.env.VITE_STORYBLOK_TOKEN,
            }),
            Storyblok.get('cdn/stories', {
              version: 'draft',
              starts_with: this.site.siteSlug,
              is_startpage: 1,
              level: 1,
              cv: Date.now(),
              token:
                this.site.storyblokToken ||
                import.meta.env.VITE_STORYBLOK_TOKEN,
            }),
          ])
          if (storiesResponse?.data?.stories.length > 0) {
            const space = spaceResponse?.data?.space
            const homepage = storiesResponse.data.stories[0]
            this.storyblokLink = `https://app.storyblok.com/#!/me/spaces/${space.id}/stories/0/0/index/${homepage.parent_id}`
          }
        }
      } catch (e) {
        console.log(e)
      }
    },
    resetState() {
      this.tagGroups = []
      this.tags = []
      this.tagErrors = []
      this.isSavedSuccessfully = false
      this.fullLogoImage = null
      this.faviconImage = null
      this.storyblokLink = null
    },
    handleError(error) {
      this.errors.push(error)
    },
  },
})
